import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import ServiceMatrix from '../components/ServiceMatrix';
import { pricingPlans, oneTimeServices } from '../data/pricingPlans';
import { CustomizationState } from '../types/pricing';
import YooKassaWidget from '../components/YooKassaWidget';

interface SubscriptionStatus {
  hasSubscription: boolean;
  plan?: string;
  endDate?: string;
  autoRenew?: boolean;
  status?: string;
  tier?: string;
  billing_period?: string;
}

interface Transaction {
  id: string;
  amount: number;
  currency: string;
  status: string;
  provider: string;
  created_at: string;
}

interface TrialInfo {
  hasActiveTrial: boolean;
  trialEndDate: string | null;
}

interface PaymentStatusData {
  subscription: SubscriptionStatus;
  transactions: Transaction[];
  trial: TrialInfo;
}

const API_URL = import.meta.env.VITE_API_URL || '';

const SubscriptionPage: React.FC = () => {
  const { user, refreshAuth } = useAuth();
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [paymentStatus, setPaymentStatus] = useState<PaymentStatusData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [processingPlan, setProcessingPlan] = useState<string | null>(null);
  const [paymentResult, setPaymentResult] = useState<string | null>(null);
  const [selectedPlan, setSelectedPlan] = useState<string>('premium');
  const [billingPeriod, setBillingPeriod] = useState<'monthly' | 'yearly'>('monthly');
  const [customizations, setCustomizations] = useState<Map<string, CustomizationState>>(new Map());
  const [showOneTime, setShowOneTime] = useState(false);
  const [changePlanModal, setChangePlanModal] = useState<{
    visible: boolean;
    tierChange: string;
    billingChange: boolean;
    credit: number;
    newCost: number;
    amountDue: number;
    endDate: string;
    remainingDays: number;
    confirmationToken?: string;
    downgradeOptions?: {
      free: { label: string; credit: number; appliedAt: string; amountDue: number };
      paid: { label: string; fullPrice: number; tier: string; billingPeriod: string };
    };
    pendingId?: string;
    appliedFree?: boolean;
  } | null>(null);
  const [paymentProcessing, setPaymentProcessing] = useState(false);
  const [widgetToken, setWidgetToken] = useState<string | null>(null);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      if (user) {
        const statusRes = await fetch(`${API_URL}/api/payment/status`, {
          credentials: 'include', // HttpOnly cookies (фаза 1.1)
        });
        const statusJson = await statusRes.json();
        if (statusJson.success) {
          setPaymentStatus(statusJson.data);
        }
      }
    } catch (err: any) {
      setError(err.message || 'Ошибка загрузки данных');
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('payment') === 'success') {
      setPaymentResult('success');
      refreshAuth();
      window.history.replaceState({}, '', '/subscription');
      // Polling для обновления статуса
      startPaymentPolling();
    } else if (params.get('payment') === 'cancelled') {
      setPaymentResult('cancelled');
      fetch(`${API_URL}/api/payment/change-plan/pending`, {
        method: 'DELETE',
        credentials: 'include', // HttpOnly cookies (фаза 1.1)
      }).catch(() => {});
    }
  }, [refreshAuth]);

  // Авто-обновление статуса при загрузке если есть paymentResult
  useEffect(() => {
    if (paymentResult) {
      setTimeout(() => loadData(), 1000);
    }
  }, [paymentResult]);

  const handleCustomize = (customization: CustomizationState) => {
    setCustomizations(prev => new Map(prev).set(customization.planSlug, customization));
  };

  const handleSubscribe = async () => {
    if (!user) {
      setError('Для оформления подписки необходимо войти в аккаунт');
      return;
    }

    const customization = customizations.get(selectedPlan);
    const plan = pricingPlans.find(p => p.slug === selectedPlan);
    if (!plan) return;

    setProcessingPlan(selectedPlan);
    setError('');

    try {
      const price = customization?.customPrice ?? (billingPeriod === 'yearly' ? plan.priceYearly : plan.priceMonthly);
      const disabledServices = customization?.disabledServices ?? [];

      const res = await fetch(`${API_URL}/api/payment/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include', // HttpOnly cookies (фаза 1.1)
        body: JSON.stringify({
          plan: selectedPlan,
          billingPeriod,
          customPrice: price,
          disabledServices,
        }),
      });

      const data = await res.json();

      if (data.success && data.data.confirmation_token) {
        setWidgetToken(data.data.confirmation_token);
      } else {
        setError(data.message || 'Ошибка создания платежа');
      }
    } catch (err: any) {
      setError(err.message || 'Ошибка соединения');
    } finally {
      setProcessingPlan(null);
    }
  };

  const handleChangePlan = async () => {
    if (!user) return;
    setProcessingPlan(selectedPlan);
    setPaymentProcessing(true);
    setError('');

    // Сначала очистим stale pending если есть
    try {
      await fetch(`${API_URL}/api/payment/change-plan/pending`, {
        method: 'DELETE',
        credentials: 'include', // HttpOnly cookies (фаза 1.1)
      });
    } catch (e) {
      // ignore — pending может не быть
    }

    try {
      const res = await fetch(`${API_URL}/api/payment/change-plan`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include', // HttpOnly cookies (фаза 1.1)
        body: JSON.stringify({
          newTier: selectedPlan,
          newBillingPeriod: billingPeriod,
        }),
      });

      const data = await res.json();

      if (data.success) {
        // Если нужна оплата — показываем модалку
        if (data.data.confirmation_token) {
          setChangePlanModal({
            visible: true,
            tierChange: data.data.tierChange,
            billingChange: data.data.billingChange,
            credit: data.data.credit,
            newCost: data.data.newCost,
            amountDue: data.data.amountDue,
            endDate: data.data.endDate,
            remainingDays: data.data.remainingDays,
            confirmationToken: data.data.confirmation_token,
            pendingId: data.data.pendingId,
          });
        } else if (data.data.appliedImmediately) {
          // Upgrade без доплаты — сразу применён
          setChangePlanModal({
            visible: true,
            tierChange: data.data.tierChange,
            billingChange: data.data.billingChange,
            credit: data.data.credit,
            newCost: data.data.newCost,
            amountDue: 0,
            endDate: '',
            remainingDays: 0,
          });
          // Polling для обновления статуса
          startPaymentPolling();
        } else if (data.data.downgradeOptions) {
          // Downgrade — показать выбор free/paid
          setChangePlanModal({
            visible: true,
            tierChange: data.data.tierChange,
            billingChange: data.data.billingChange,
            credit: data.data.credit,
            newCost: data.data.newCost,
            amountDue: 0,
            endDate: data.data.endDate,
            remainingDays: data.data.remainingDays,
            pendingId: data.data.pendingId,
            downgradeOptions: data.data.downgradeOptions,
          });
        } else {
          // Downgrade scheduled (старый fallback)
          setChangePlanModal({
            visible: true,
            tierChange: data.data.tierChange,
            billingChange: data.data.billingChange,
            credit: data.data.credit,
            newCost: data.data.newCost,
            amountDue: 0,
            endDate: data.data.endDate,
            remainingDays: data.data.remainingDays,
            pendingId: data.data.pendingId,
          });
          setTimeout(() => loadData(), 2000);
        }
      } else {
        setError(data.message || 'Ошибка смены тарифа');
      }
    } catch (err: any) {
      setError(err.message || 'Ошибка соединения');
    } finally {
      setProcessingPlan(null);
      setPaymentProcessing(false);
    }
  };

  const startPaymentPolling = async () => {
    let attempts = 0;
    const maxAttempts = 15; // 30 сек

    const poll = async () => {
      if (attempts >= maxAttempts) {
        setPaymentProcessing(false);
        return;
      }
      attempts++;

      try {
        const res = await fetch(`${API_URL}/api/payment/status`, {
          credentials: 'include', // HttpOnly cookies (фаза 1.1)
        });
        const json = await res.json();
        if (json.success) {
          setPaymentStatus(json.data);
          // Проверяем что tier изменился
          const newTier = json.data.subscription.plan?.toLowerCase();
          if (newTier && newTier !== paymentStatus?.subscription.plan?.toLowerCase()) {
            setPaymentProcessing(false);
            setChangePlanModal(null);
            return;
          }
        }
      } catch (e) {
        // ignore
      }

      setTimeout(poll, 2000);
    };

    poll();
  };

  const handleOneTimePurchase = async (serviceId: string) => {
    if (!user) {
      setError('Для покупки необходимо войти в аккаунт');
      return;
    }

    setProcessingPlan(serviceId);
    setError('');

    try {
      const res = await fetch(`${API_URL}/api/payment/one-time`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include', // HttpOnly cookies (фаза 1.1)
        body: JSON.stringify({ serviceId }),
      });
      const data = await res.json();

      if (data.success && data.data.confirmation_token) {
        setWidgetToken(data.data.confirmation_token);
      } else {
        setError(data.message || 'Ошибка создания платежа');
      }
    } catch (err: any) {
      setError(err.message || 'Ошибка соединения');
    } finally {
      setProcessingPlan(null);
    }
  };

  const cardStyle: React.CSSProperties = {
    background: 'rgba(13, 20, 40, 0.85)',
    backdropFilter: 'blur(20px)',
    border: '1px solid rgba(212, 175, 55, 0.12)',
    borderRadius: '24px',
    padding: isMobile ? '20px 16px' : '32px 28px',
    position: 'relative',
    overflow: 'hidden',
    boxShadow: '0 8px 40px rgba(212, 175, 55, 0.08), inset 0 0 40px rgba(13, 20, 40, 0.5)',
  };

  if (loading && !paymentStatus) {
    return (
      <div style={{
        minHeight: '60vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#d4af37',
        fontSize: '18px',
      }}>
        Загрузка...
      </div>
    );
  }

  return (
    <div style={{
      maxWidth: '1200px',
      margin: '0 auto',
      padding: isMobile ? '24px 12px 60px' : '40px 20px 80px',
      position: 'relative',
    }}>
      <div style={{ position: 'absolute', top: 0, left: '10%', right: '10%', height: '2px', background: 'linear-gradient(90deg, transparent, #d4af37, transparent)' }} />
      {/* Header */}
      <div style={{ textAlign: 'center', marginBottom: isMobile ? '32px' : '48px' }}>
        <h1 style={{
          fontFamily: 'var(--font-display)',
          fontStyle: 'italic',
          fontSize: isMobile ? '26px' : '36px',
          fontWeight: 'bold',
          background: 'linear-gradient(135deg, #F4E4A6 0%, #D4AF37 40%, #B8942B 70%, #F4E4A6 100%)',
          backgroundSize: '200% auto',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          backgroundClip: 'text',
          marginBottom: '12px',
        }}>
          Выберите свой план Astranum
        </h1>
        <p style={{ color: '#b0b0b0', fontSize: isMobile ? '14px' : '16px', maxWidth: '700px', margin: '0 auto 8px' }}>
          Персонализированная астрология и нумерология по подписке. Научный подход, точные расчёты Swiss Ephemeris, современный UX.
        </p>
        <p style={{ color: '#b0b0b0', fontSize: isMobile ? '12px' : '14px', maxWidth: '600px', margin: '0 auto' }}>
          80% пользователей выбирают Premium · Отмена в любой момент · Бесплатный триал 7 дней
        </p>
      </div>

      {/* Billing period toggle */}
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        marginBottom: '32px',
        gap: '8px',
      }}>
        <button
          onClick={() => setBillingPeriod('monthly')}
          style={{
            padding: '10px 24px',
            borderRadius: '12px',
            border: billingPeriod === 'monthly' ? '2px solid #d4af37' : '1px solid #444',
            background: billingPeriod === 'monthly' ? 'rgba(212, 175, 55, 0.2)' : 'transparent',
            color: billingPeriod === 'monthly' ? '#d4af37' : '#aaa',
            fontSize: '15px',
            fontWeight: 600,
            cursor: 'pointer',
            transition: 'all 0.2s',
            fontFamily: 'var(--font-sans)',
          }}
        >
          Ежемесячно
        </button>
        <button
          onClick={() => setBillingPeriod('yearly')}
          style={{
            padding: '10px 24px',
            borderRadius: '12px',
            border: billingPeriod === 'yearly' ? '2px solid #4fd1c5' : '1px solid #444',
            background: billingPeriod === 'yearly' ? 'rgba(79, 209, 197, 0.2)' : 'transparent',
            color: billingPeriod === 'yearly' ? '#4fd1c5' : '#aaa',
            fontSize: '15px',
            fontWeight: 600,
            cursor: 'pointer',
            transition: 'all 0.2s',
            fontFamily: 'var(--font-sans)',
            position: 'relative',
          }}
        >
          Ежегодно
          <span style={{
            position: 'absolute',
            top: '-8px',
            right: '-8px',
            background: 'rgba(79, 209, 197, 0.3)',
            color: '#4fd1c5',
            fontSize: '11px',
            padding: '2px 6px',
            borderRadius: '6px',
            fontWeight: 700,
          }}>
            -30%
          </span>
        </button>
      </div>

      {/* Current status */}
      {paymentStatus && (
        <div style={{
          ...cardStyle,
          marginBottom: '40px',
          borderColor: paymentStatus.subscription.hasSubscription
            ? 'rgba(79, 209, 197, 0.5)'
            : 'rgba(212, 175, 55, 0.3)',
        }}>
          <h3 style={{ color: '#f0f0f0', fontSize: isMobile ? '18px' : '20px', marginBottom: '16px' }}>
            Ваш текущий статус
          </h3>
          {paymentStatus.subscription.hasSubscription ? (
            <div>
              <p style={{ color: '#4fd1c5', fontSize: '16px', marginBottom: '8px' }}>
                ✅ Активная подписка
              </p>
              <div style={{ 
                display: 'flex', 
                gap: '24px', 
                flexWrap: 'wrap',
                marginTop: '12px',
                paddingTop: '12px',
                borderTop: '1px solid rgba(255,255,255,0.1)'
              }}>
                <div>
                  <p style={{ color: '#888', fontSize: '12px', margin: '0 0 4px' }}>Тариф</p>
                  <p style={{ 
                    color: '#fff', 
                    fontSize: '16px', 
                    fontWeight: 600,
                    margin: 0,
                    padding: '4px 12px',
                    background: paymentStatus.subscription.tier === 'pro' ? 'rgba(155, 89, 182, 0.2)' :
                               paymentStatus.subscription.tier === 'premium' ? 'rgba(212, 175, 55, 0.2)' : 'rgba(79, 209, 197, 0.2)',
                    borderRadius: '8px',
                    display: 'inline-block'
                  }}>
                    {paymentStatus.subscription.tier === 'pro' ? 'Pro' : 
                     paymentStatus.subscription.tier === 'premium' ? 'Premium' : 
                     paymentStatus.subscription.tier === 'base' ? 'Base' : 
                     paymentStatus.subscription.plan || '—'}
                  </p>
                </div>
                <div>
                  <p style={{ color: '#888', fontSize: '12px', margin: '0 0 4px' }}>Действует до</p>
                  <p style={{ color: '#fff', fontSize: '16px', fontWeight: 600, margin: 0 }}>
                    {paymentStatus.subscription.endDate
                      ? new Date(paymentStatus.subscription.endDate).toLocaleDateString('ru-RU', {
                          day: 'numeric',
                          month: 'long',
                          year: 'numeric'
                        })
                      : '—'}
                  </p>
                </div>
                <div>
                  <p style={{ color: '#888', fontSize: '12px', margin: '0 0 4px' }}>Период</p>
                  <p style={{ color: '#fff', fontSize: '16px', fontWeight: 600, margin: 0 }}>
                    {paymentStatus.subscription.billing_period === 'yearly' ? 'Годовой' : 'Месячный'}
                  </p>
                </div>
                {paymentStatus.subscription.autoRenew && (
                  <div>
                    <p style={{ color: '#888', fontSize: '12px', margin: '0 0 4px' }}>Статус</p>
                    <p style={{ color: '#4fd1c5', fontSize: '14px', fontWeight: 600, margin: 0 }}>
                      ✓ Автопродление
                    </p>
                  </div>
                )}
              </div>
            </div>
          ) : paymentStatus.trial.hasActiveTrial ? (
            <div>
              <p style={{ color: '#f4d03f', fontSize: '16px', marginBottom: '8px' }}>
                🎁 Бесплатный триал активен
              </p>
              <p style={{ color: '#aaa', fontSize: '14px' }}>
                Действует до:{' '}
                {paymentStatus.trial.trialEndDate
                  ? new Date(paymentStatus.trial.trialEndDate).toLocaleDateString('ru-RU')
                  : '—'}
              </p>
            </div>
          ) : (
            <p style={{ color: '#b0b0b0', fontSize: '14px' }}>
              У вас нет активной подписки. Выберите тариф ниже.
            </p>
          )}

          {paymentStatus.transactions.length > 0 && (
            <div style={{ marginTop: '20px', borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: '16px' }}>
              <h4 style={{ color: '#f0f0f0', fontSize: '16px', marginBottom: '12px' }}>
                Последние транзакции
              </h4>
              {paymentStatus.transactions.slice(0, 5).map((tx) => (
                <div key={tx.id} style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  padding: '8px 0',
                  borderBottom: '1px solid rgba(255,255,255,0.05)',
                }}>
                  <span style={{ color: '#aaa', fontSize: '14px' }}>
                    {new Date(tx.created_at).toLocaleDateString('ru-RU')}
                  </span>
                  <span style={{ color: '#fff', fontSize: '14px' }}>
                    {tx.amount} {tx.currency}
                  </span>
                  <span style={{
                    fontSize: '13px',
                    color: tx.status === 'success' ? '#4fd1c5' :
                           tx.status === 'pending' ? '#f4d03f' : '#dc3545',
                  }}>
                    {tx.status === 'success' ? 'Успешно' :
                     tx.status === 'pending' ? 'В обработке' :
                     tx.status === 'failed' ? 'Ошибка' : tx.status}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Payment result modal */}
      {paymentResult && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center',
          justifyContent: 'center', zIndex: 1000,
        }} onClick={() => setPaymentResult(null)}>
          <div style={{
            background: 'rgba(13, 20, 40, 0.85)',
            backdropFilter: 'blur(20px)', border: '1px solid rgba(212, 175, 55, 0.12)',
            borderRadius: '24px', padding: isMobile ? '24px 16px' : '32px', maxWidth: '420px', width: '90%',
            textAlign: 'center', color: '#fff', position: 'relative', overflow: 'hidden',
            boxShadow: '0 8px 40px rgba(212, 175, 55, 0.08), inset 0 0 40px rgba(13, 20, 40, 0.5)',
          }} onClick={e => e.stopPropagation()}>
            <div style={{ position: 'absolute', top: 0, left: '10%', right: '10%', height: '2px', background: 'linear-gradient(90deg, transparent, #d4af37, transparent)' }} />
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>
              {paymentResult === 'success' ? '✅' : '❌'}
            </div>
            <h3 style={{ color: '#d4af37', fontSize: '22px', marginBottom: '12px' }}>
              {paymentResult === 'success' ? 'Оплата прошла успешно!' : 'Оплата отменена'}
            </h3>
            {paymentResult === 'success' ? (
              <>
                <p style={{ color: '#aaa', fontSize: '15px', marginBottom: '16px' }}>
                  Ваш платёж обработан. Подписка активирована.
                </p>
                <div style={{
                  padding: '12px',
                  background: 'rgba(79, 209, 197, 0.1)',
                  borderRadius: '8px',
                  border: '1px solid rgba(79, 209, 197, 0.3)',
                  marginBottom: '16px',
                }}>
                  <p style={{ color: '#4fd1c5', fontSize: '14px', margin: 0 }}>
                    📧 Чек будет отправлен на ваш email в течение 5 минут.
                  </p>
                </div>
              </>
            ) : (
              <p style={{ color: '#aaa', fontSize: '15px', marginBottom: '24px' }}>
                Вы можете попробовать снова в любое время.
              </p>
            )}
            <button onClick={() => setPaymentResult(null)} style={{
              padding: '14px 40px', borderRadius: '12px', border: 'none',
              background: 'rgba(212, 175, 55, 0.2)', color: '#d4af37',
              fontSize: '16px', fontWeight: 600, cursor: 'pointer',
            }}>
              Закрыть
            </button>
          </div>
        </div>
      )}

      {/* Error */}
      {error && (
        <div style={{
          background: 'rgba(220, 53, 69, 0.2)',
          border: '1px solid #dc3545',
          borderRadius: '12px',
          padding: '16px',
          marginBottom: '24px',
          color: '#dc3545',
          textAlign: 'center',
        }}>
          {error}
        </div>
      )}

      {/* Plans grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
        gap: '24px',
        marginBottom: '48px',
      }}>
        {pricingPlans.filter(p => p.slug !== 'free').map((plan) => (
          <ServiceMatrix
            key={plan.id}
            plan={plan}
            isSelected={selectedPlan === plan.slug}
            onSelect={setSelectedPlan}
            onCustomize={handleCustomize}
            billingPeriod={billingPeriod}
          />
        ))}
      </div>

      {/* One-time services toggle */}
      <div style={{ textAlign: 'center', marginBottom: '24px' }}>
        <button
          onClick={() => setShowOneTime(!showOneTime)}
          style={{
            padding: '12px 28px',
            borderRadius: '12px',
            border: '1px solid rgba(212, 175, 55, 0.4)',
            background: showOneTime ? 'rgba(212, 175, 55, 0.2)' : 'transparent',
            color: '#d4af37',
            fontSize: '15px',
            fontWeight: 600,
            cursor: 'pointer',
            transition: 'all 0.2s',
            fontFamily: 'var(--font-sans)',
          }}
        >
          {showOneTime ? 'Скрыть разовые услуги' : '🛒 Разовые услуги (без подписки)'}
        </button>
      </div>

      {/* One-time services */}
      {showOneTime && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          gap: '16px',
          marginBottom: '48px',
        }}>
          {oneTimeServices.map(service => {
            const isProcessing = processingPlan === service.id;
            return (
              <div
                key={service.id}
                style={{
                  background: 'rgba(13, 20, 40, 0.85)',
                  backdropFilter: 'blur(20px)',
                  border: '1px solid rgba(212, 175, 55, 0.12)',
                  borderRadius: '24px',
                  padding: isMobile ? '16px' : '24px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '12px',
                  boxShadow: '0 8px 40px rgba(212, 175, 55, 0.08), inset 0 0 40px rgba(13, 20, 40, 0.5)',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <span style={{ fontSize: '24px' }}>{service.icon}</span>
                  <div>
                    <h4 style={{ color: '#fff', fontSize: '16px', margin: 0 }}>{service.name}</h4>
                    <p style={{ color: '#b0b0b0', fontSize: '13px', margin: '4px 0 0' }}>{service.description}</p>
                  </div>
                </div>
                <div style={{ color: '#d4af37', fontSize: '24px', fontWeight: 'bold' }}>
                  {service.price} ₽
                </div>
                <button
                  onClick={() => handleOneTimePurchase(service.id)}
                  disabled={isProcessing}
                  style={{
                    width: '100%',
                    padding: '12px',
                    borderRadius: '10px',
                    border: '1px solid rgba(212, 175, 55, 0.4)',
                    background: isProcessing ? '#666' : 'rgba(212, 175, 55, 0.15)',
                    color: '#d4af37',
                    fontSize: '14px',
                    fontWeight: 600,
                    cursor: isProcessing ? 'not-allowed' : 'pointer',
                    transition: 'all 0.2s',
                    fontFamily: 'var(--font-sans)',
                  }}
                >
                  {isProcessing ? 'Обработка...' : 'Купить'}
                </button>
              </div>
            );
          })}
        </div>
      )}

      {/* CTA for selected plan */}
      <div style={{
        textAlign: 'center',
        marginTop: '32px',
      }}>
        <button
          onClick={paymentStatus?.subscription.hasSubscription ? handleChangePlan : handleSubscribe}
          disabled={processingPlan !== null || paymentProcessing}
          style={{
            padding: '16px 48px',
            borderRadius: '16px',
            border: 'none',
            background: (processingPlan || paymentProcessing)
              ? '#666'
              : 'linear-gradient(135deg, #d4af37 0%, #b8941f 100%)',
            color: '#0a0a1a',
            fontSize: '18px',
            fontWeight: 700,
            cursor: (processingPlan || paymentProcessing) ? 'not-allowed' : 'pointer',
            transition: 'all 0.3s',
            fontFamily: 'var(--font-sans)',
            boxShadow: '0 4px 20px rgba(212, 175, 55, 0.3)',
          }}
        >
          {(processingPlan || paymentProcessing)
            ? 'Обработка...'
            : paymentStatus?.subscription.hasSubscription
              ? `Сменить на ${selectedPlan === 'base' ? 'Base' : selectedPlan === 'premium' ? 'Premium' : 'Pro'}`
              : `Оформить ${selectedPlan === 'base' ? 'Base' : selectedPlan === 'premium' ? 'Premium' : 'Pro'} — ${
                  customizations.get(selectedPlan)?.customPrice ??
                  pricingPlans.find(p => p.slug === selectedPlan)?.[billingPeriod === 'yearly' ? 'priceYearly' : 'priceMonthly']
                } ₽/${billingPeriod === 'yearly' ? 'год' : 'мес'}`}
        </button>
        <p style={{ color: '#b0b0b0', fontSize: isMobile ? '12px' : '13px', marginTop: '12px' }}>
          Отмена в любой момент · 7 дней бесплатного триала · Безопасная оплата через ЮKassa
        </p>
      </div>

      {/* Change Plan Modal */}
      {changePlanModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
        }} onClick={() => !paymentProcessing && setChangePlanModal(null)}>
          <div style={{
            background: 'rgba(13, 20, 40, 0.85)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(212, 175, 55, 0.12)',
            borderRadius: '24px',
            padding: isMobile ? '24px 16px' : '32px',
            maxWidth: '480px',
            width: '90%',
            color: '#fff',
            position: 'relative',
            overflow: 'hidden',
            boxShadow: '0 8px 40px rgba(212, 175, 55, 0.08), inset 0 0 40px rgba(13, 20, 40, 0.5)',
          }} onClick={(e) => e.stopPropagation()}>
            <div style={{ position: 'absolute', top: 0, left: '10%', right: '10%', height: '2px', background: 'linear-gradient(90deg, transparent, #d4af37, transparent)' }} />
            <h3 style={{ color: '#d4af37', fontSize: '22px', marginBottom: '20px' }}>
              {changePlanModal.confirmationToken ? 'Оплата смены тарифа' :
               changePlanModal.amountDue === 0 && changePlanModal.tierChange === 'upgrade' ? 'Тариф изменён!' :
               'Смена тарифа'}
            </h3>

            {paymentProcessing ? (
              <div style={{ textAlign: 'center', padding: '20px 0' }}>
                <div style={{ fontSize: '32px', marginBottom: '12px' }}>⏳</div>
                <p style={{ color: '#aaa' }}>Оплата обрабатывается...</p>
              </div>
            ) : (
              <>
                <div style={{ marginBottom: '20px' }}>
                  {changePlanModal.tierChange === 'upgrade' && changePlanModal.amountDue > 0 && (
                    <>
                      <p style={{ color: '#4fd1c5', fontSize: '16px' }}>
                        Переход на {changePlanModal.tierChange === 'upgrade' ? 'более высокий' : 'другой'} тариф произойдёт сейчас
                      </p>
                      <div style={{ marginTop: '12px', padding: '12px', background: 'rgba(255,255,255,0.05)', borderRadius: '8px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                          <span style={{ color: '#aaa' }}>Кредит за {changePlanModal.remainingDays} дн.:</span>
                          <span style={{ color: '#4fd1c5' }}>-{changePlanModal.credit}₽</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                          <span style={{ color: '#aaa' }}>Стоимость нового тарифа:</span>
                          <span>{changePlanModal.newCost}₽</span>
                        </div>
                        <div style={{ borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: '8px', display: 'flex', justifyContent: 'space-between' }}>
                          <span style={{ color: '#d4af37', fontWeight: 'bold' }}>Итого к доплате:</span>
                          <span style={{ color: '#d4af37', fontWeight: 'bold', fontSize: '18px' }}>{changePlanModal.amountDue}₽</span>
                        </div>
                      </div>
                    </>
                  )}

                  {changePlanModal.tierChange === 'upgrade' && changePlanModal.amountDue === 0 && (
                    <div style={{ padding: '16px', background: 'rgba(79, 209, 197, 0.15)', borderRadius: '8px', border: '1px solid rgba(79, 209, 197, 0.3)' }}>
                      <p style={{ color: '#4fd1c5', fontSize: '16px', margin: 0 }}>
                        ✅ Переход выполнен! Кредит {changePlanModal.credit}₽ полностью покрыл стоимость нового тарифа.
                      </p>
                    </div>
                  )}

                  {changePlanModal.tierChange === 'downgrade' && changePlanModal.downgradeOptions && (
                    <div>
                      <div style={{
                        padding: '16px',
                        background: 'rgba(244, 208, 63, 0.15)',
                        borderRadius: '12px',
                        border: '1px solid rgba(244, 208, 63, 0.3)',
                        marginBottom: '16px',
                      }}>
                        <p style={{ color: '#f4d03f', fontSize: '16px', fontWeight: 600, margin: '0 0 8px' }}>
                          ⚠️ Внимание!
                        </p>
                        <p style={{ color: '#aaa', fontSize: '14px', margin: 0 }}>
                          При понижении тарифа разница не возвращается.
                          Ваш кредит за {changePlanModal.remainingDays} дн. составляет <strong style={{ color: '#4fd1c5' }}>{changePlanModal.credit}₽</strong>.
                        </p>
                      </div>

                      <div style={{ marginBottom: '16px' }}>
                        <p style={{ color: '#fff', fontSize: '15px', marginBottom: '12px' }}>Выберите вариант:</p>

                        {/* Free вариант */}
                        <button
                          onClick={async () => {
                            setPaymentProcessing(true);
                            try {
                              const res = await fetch(`${API_URL}/api/payment/change-plan/confirm`, {
                                method: 'POST',
                                headers: { 'Content-Type': 'application/json' },
                                credentials: 'include', // HttpOnly cookies (фаза 1.1)
                                body: JSON.stringify({ pendingId: changePlanModal.pendingId, mode: 'free' }),
                              });
                              const data = await res.json();
                              if (data.success) {
                                setChangePlanModal({ ...changePlanModal, appliedFree: true });
                                setTimeout(() => loadData(), 2000);
                              } else {
                                setError(data.message || 'Ошибка');
                              }
                            } catch (e: any) {
                              setError(e.message || 'Ошибка соединения');
                            } finally {
                              setPaymentProcessing(false);
                            }
                          }}
                          disabled={paymentProcessing}
                          style={{
                            width: '100%',
                            padding: '14px',
                            borderRadius: '12px',
                            border: '1px solid rgba(79, 209, 197, 0.4)',
                            background: 'rgba(79, 209, 197, 0.15)',
                            color: '#4fd1c5',
                            fontSize: '15px',
                            fontWeight: 600,
                            cursor: paymentProcessing ? 'not-allowed' : 'pointer',
                            marginBottom: '8px',
                          }}
                        >
                          Использовать кредит — тариф изменится {new Date(changePlanModal.endDate).toLocaleDateString('ru-RU')}
                        </button>

                        {/* Paid вариант */}
                        <button
                          onClick={async () => {
                            setPaymentProcessing(true);
                            try {
                              const res = await fetch(`${API_URL}/api/payment/change-plan/confirm`, {
                                method: 'POST',
                                headers: { 'Content-Type': 'application/json' },
                                credentials: 'include', // HttpOnly cookies (фаза 1.1)
                                body: JSON.stringify({ pendingId: changePlanModal.pendingId, mode: 'paid' }),
                              });
                              const data = await res.json();
                              if (data.success && data.data.confirmation_token) {
                                setWidgetToken(data.data.confirmation_token);
                                setChangePlanModal(null);
                              } else {
                                setError(data.message || 'Ошибка создания платежа');
                              }
                            } catch (e: any) {
                              setError(e.message || 'Ошибка соединения');
                            } finally {
                              setPaymentProcessing(false);
                            }
                          }}
                          disabled={paymentProcessing}
                          style={{
                            width: '100%',
                            padding: '14px',
                            borderRadius: '12px',
                            border: 'none',
                            background: 'linear-gradient(135deg, #d4af37 0%, #b8941f 100%)',
                            color: '#0a0a1a',
                            fontSize: '15px',
                            fontWeight: 700,
                            cursor: paymentProcessing ? 'not-allowed' : 'pointer',
                          }}
                        >
                          Оплатить новый тариф — {changePlanModal.downgradeOptions.paid.fullPrice}₽
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                {changePlanModal.confirmationToken ? (
                  <div style={{ display: 'flex', gap: '12px' }}>
                    <button
                      onClick={() => {
                        if (changePlanModal.confirmationToken) {
                          setWidgetToken(changePlanModal.confirmationToken);
                          setChangePlanModal(null);
                        }
                      }}
                      style={{
                        flex: 1,
                        padding: '14px',
                        borderRadius: '12px',
                        border: 'none',
                        background: 'linear-gradient(135deg, #d4af37 0%, #b8941f 100%)',
                        color: '#0a0a1a',
                        fontSize: '16px',
                        fontWeight: 700,
                        cursor: 'pointer',
                      }}
                    >
                      Оплатить {changePlanModal.amountDue}₽
                    </button>
                    <button
                      onClick={() => setChangePlanModal(null)}
                      style={{
                        padding: '14px 24px',
                        borderRadius: '12px',
                        border: '1px solid #444',
                        background: 'transparent',
                        color: '#aaa',
                        fontSize: '14px',
                        cursor: 'pointer',
                      }}
                    >
                      Отмена
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => setChangePlanModal(null)}
                    style={{
                      width: '100%',
                      padding: '14px',
                      borderRadius: '12px',
                      border: 'none',
                      background: 'rgba(212, 175, 55, 0.2)',
                      color: '#d4af37',
                      fontSize: '16px',
                      fontWeight: 600,
                      cursor: 'pointer',
                    }}
                  >
                    Закрыть
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* YooKassa Payment Widget Modal */}
      {widgetToken && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10002,
            backdropFilter: 'blur(5px)',
          }}
          onClick={() => setWidgetToken(null)}
        >
          <div
            style={{
              background: 'rgba(13, 20, 40, 0.95)',
              backdropFilter: 'blur(20px)',
              border: '1px solid rgba(212, 175, 55, 0.12)',
              borderRadius: '24px',
              padding: isMobile ? '16px' : '24px',
              maxWidth: '600px',
              width: '90%',
              maxHeight: '90vh',
              overflow: 'auto',
              boxShadow: '0 8px 40px rgba(212, 175, 55, 0.08), inset 0 0 40px rgba(13, 20, 40, 0.5)',
              position: 'relative',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ position: 'absolute', top: 0, left: '10%', right: '10%', height: '2px', background: 'linear-gradient(90deg, transparent, #d4af37, transparent)' }} />
            <h3 style={{
              fontSize: isMobile ? '18px' : '22px',
              fontFamily: 'var(--font-display)',
              fontStyle: 'italic',
              textAlign: 'center',
              marginBottom: '16px',
              background: 'linear-gradient(135deg, #F4E4A6 0%, #D4AF37 40%, #B8942B 70%, #F4E4A6 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}>
              Оплата
            </h3>
            <YooKassaWidget
              confirmation_token={widgetToken}
              onSuccess={() => {
                setWidgetToken(null);
                refreshAuth();
                loadData();
                setPaymentResult('success');
              }}
              onError={(err) => {
                console.error('[SubscriptionPage] Widget error:', err);
                setError('Ошибка оплаты');
                setWidgetToken(null);
              }}
            />
            <button
              onClick={() => setWidgetToken(null)}
              style={{
                position: 'absolute',
                top: '12px',
                right: '12px',
                background: 'none',
                border: 'none',
                color: '#888',
                fontSize: '20px',
                cursor: 'pointer',
              }}
            >
              ✕
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubscriptionPage;
